import Pusher, { Channel } from "pusher-js";
import { authStorageService } from "@/features/auth/services/storage.service";

const key = import.meta.env.VITE_PUSHER_KEY;
const cluster = import.meta.env.VITE_PUSHER_CLUSTER;
const legacyAuthEndpoint = import.meta.env.VITE_PUSHER_AUTH_ENDPOINT || `${import.meta.env.VITE_API_URL}/pusher/auth`;
const chatAuthEndpoint = `${import.meta.env.VITE_API_URL}/chat/pusher/auth`;

type EventCallback<T> = (data: T) => void;

export type PushService = {
  subscribe<T>(channelName: string, eventName: string, callback: EventCallback<T>): void
  unsubscribe<T>(channelName: string, eventName?: string, callback?: EventCallback<T>): void
  trigger(channelName: string, eventName: string, data: unknown): void
  disconnect(): void
  reconnect(): void
  getConnectionState(): string
};

type CallbackRegistry = Map<string, Map<string, Set<EventCallback<unknown>>>>;

export class PusherService implements PushService {
  private readonly pusher: Pusher;
  private readonly channels: Map<string, Channel>;
  private readonly callbackRegistry: CallbackRegistry;

  constructor(key: string, cluster: string, _authEndpoint?: string) {
    this.pusher = new Pusher(key, {
      cluster,
      auth: { headers: {} },
      authorizer: (channel) => ({
        authorize: (socketId, callback) => {
          const token = authStorageService.getAuthToken();

          if (!token) {
            callback(new Error("No access token found"), null);
            return;
          }

          // Route chat v2 channels to dedicated auth endpoint
          const isChatChannel = channel.name.startsWith("presence-chat-")
            || channel.name.startsWith("presence-org-")
            || channel.name.startsWith("private-employee_notifications_");
          const endpoint = isChatChannel ? chatAuthEndpoint : legacyAuthEndpoint;

          const params = new URLSearchParams();
          params.append("socket_id", socketId);
          params.append("channel_name", channel.name);

          // Hard cap on the auth round-trip so a hung backend doesn't
          // freeze the Pusher connection (no native timeout on fetch).
          const controller = new AbortController();
          const timeoutId = window.setTimeout(() => controller.abort(), 10_000);

          fetch(`${endpoint}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Authorization": `Bearer ${token}`,
            },
            body: params,
            credentials: "include",
            signal: controller.signal,
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Auth failed: ${response.status}`);
              }
              return response.json();
            })
            .then((data) => {
              callback(null, data);
            })
            .catch((error) => {
              callback(error, null);
            })
            .finally(() => {
              window.clearTimeout(timeoutId);
            });
        },
      }),
    });

    this.channels = new Map<string, Channel>();
    this.callbackRegistry = new Map<string, Map<string, Set<EventCallback<unknown>>>>();
  }

  subscribe<T>(channelName: string, eventName: string, callback: EventCallback<T>): void {
    const channel = this.getOrCreateChannel(channelName);

    const channelCallbacks = this.getOrCreateChannelCallbacks(channelName);
    const eventCallbacks = this.getOrCreateEventCallbacks(channelCallbacks, eventName);

    if (eventCallbacks.has(callback as EventCallback<unknown>)) {
      return;
    }

    this.registerCallback(channelName, eventName, callback);
    channel.bind(eventName, callback);
  }

  unsubscribe<T>(channelName: string, eventName?: string, callback?: EventCallback<T>): void {
    if (!this.hasChannel(channelName)) {
      return;
    }

    if (this.shouldUnsubscribeFromEntireChannel(eventName, callback as EventCallback<unknown>)) {
      this.unsubscribeFromEntireChannel(channelName);
      return;
    }

    if (eventName && callback) {
      this.unsubscribeFromSpecificEvent(channelName, eventName, callback);
    }
  }

  trigger(channelName: string, eventName: string, data: unknown): void {
    const channel = this.channels.get(channelName);
    if (channel) {
      channel.trigger(eventName, data);
    }
  }

  disconnect(): void {
    this.pusher.disconnect();
    this.clearAllRegistries();
  }

  reconnect(): void {
    if (this.isConnected()) {
      this.pusher.disconnect();
    }
    this.pusher.connect();
  }

  getConnectionState(): string {
    return this.pusher.connection.state;
  }

  private getOrCreateChannel(channelName: string): Channel {
    let channel = this.channels.get(channelName);
    if (!channel) {
      channel = this.pusher.subscribe(channelName);
      this.channels.set(channelName, channel);
    }
    return channel;
  }

  private registerCallback<T>(channelName: string, eventName: string, callback: EventCallback<T>): void {
    const channelCallbacks = this.getOrCreateChannelCallbacks(channelName);
    const eventCallbacks = this.getOrCreateEventCallbacks(channelCallbacks, eventName);
    eventCallbacks.add(callback as EventCallback<unknown>);
  }

  private getOrCreateChannelCallbacks(channelName: string): Map<string, Set<EventCallback<unknown>>> {
    if (!this.callbackRegistry.has(channelName)) {
      this.callbackRegistry.set(channelName, new Map());
    }
    return this.callbackRegistry.get(channelName) ?? new Map();
  }

  private getOrCreateEventCallbacks(
    channelCallbacks: Map<string, Set<EventCallback<unknown>>>,
    eventName: string,
  ): Set<EventCallback<unknown>> {
    if (!channelCallbacks.has(eventName)) {
      channelCallbacks.set(eventName, new Set());
    }
    return channelCallbacks.get(eventName) ?? new Set();
  }

  private hasChannel(channelName: string): boolean {
    return this.channels.has(channelName);
  }

  private shouldUnsubscribeFromEntireChannel(eventName?: string, callback?: EventCallback<unknown>): boolean {
    return !eventName || !callback;
  }

  private unsubscribeFromEntireChannel(channelName: string): void {
    this.pusher.unsubscribe(channelName);
    this.channels.delete(channelName);
    this.callbackRegistry.delete(channelName);
  }

  private unsubscribeFromSpecificEvent<T>(
    channelName: string,
    eventName: string,
    callback: EventCallback<T>,
  ): void {
    const channel = this.channels.get(channelName);
    const channelCallbacks = this.callbackRegistry.get(channelName);

    if (!channel || !channelCallbacks) return;

    this.removeCallbackFromRegistry(channelCallbacks, eventName, callback);
    this.cleanupEmptyRegistries(channelName, channelCallbacks);
    channel.unbind(eventName, callback);
  }

  private removeCallbackFromRegistry<T>(
    channelCallbacks: Map<string, Set<EventCallback<unknown>>>,
    eventName: string,
    callback: EventCallback<T>,
  ): void {
    const eventCallbacks = channelCallbacks.get(eventName);
    if (eventCallbacks) {
      eventCallbacks.delete(callback as EventCallback<unknown>);
    }
  }

  private cleanupEmptyRegistries(
    channelName: string,
    channelCallbacks: Map<string, Set<EventCallback<unknown>>>,
  ): void {
    this.removeEmptyEventCallbacks(channelCallbacks);
    this.removeEmptyChannelCallbacks(channelName, channelCallbacks);
  }

  private removeEmptyEventCallbacks(channelCallbacks: Map<string, Set<EventCallback<unknown>>>): void {
    for (const [eventName, eventCallbacks] of channelCallbacks.entries()) {
      if (eventCallbacks.size === 0) {
        channelCallbacks.delete(eventName);
      }
    }
  }

  private removeEmptyChannelCallbacks(
    channelName: string,
    channelCallbacks: Map<string, Set<EventCallback<unknown>>>,
  ): void {
    if (channelCallbacks.size === 0) {
      this.callbackRegistry.delete(channelName);
    }
  }

  private isConnected(): boolean {
    return this.pusher.connection.state === "connected";
  }

  private clearAllRegistries(): void {
    this.channels.clear();
    this.callbackRegistry.clear();
  }
}

export const defaultPushService = new PusherService(key, cluster, legacyAuthEndpoint);
